import InvoiceSelect from "components/FormControls/ThemedSelect";
import { useEffect, useMemo, useState } from "react";
import { LuCheck, LuCreditCard, LuLoaderCircle, LuX } from "react-icons/lu";
import PickDate from "components/DatePicker/DatePicker";
import Modal from "@mui/material/Modal";

const PAYMENT_METHODS = [
  "Bank Transfer",
  "Cash",
  "Card",
  "Cheque",
  "Paypal",
  "Wire Transfer",
];

function RCTISettlementDialog({
  open,
  dark,
  values,
  currencyCode,
  outstanding,
  paidAmount,
  result,
  onClose,
  onContinue,
  onView,
}) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Bank Transfer");
  const [paymentDate, setPaymentDate] = useState("");
  const [reference, setReference] = useState("");
  const [errors, setErrors] = useState({});

  const money = useMemo(
    () =>
      new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: /^[A-Z]{3}$/.test(currencyCode || "") ? currencyCode : "AUD",
      }),
    [currencyCode]
  );
  const rctiTotal = Number(values.finalAmount ?? values.totalAmount ?? 0);
  const fullSettlement =
    paidAmount <= 0 && Math.abs(Number(outstanding) - rctiTotal) < 0.005;
  const supplierName =
    values.supplierCompanyName || values.supplierName || "Supplier";

  useEffect(() => {
    if (!open) return;
    setAmount(
      Number(
        result?.type === "success"
          ? result.amount
          : result?.draft?.amount || outstanding || 0
      ).toFixed(2)
    );
    setMethod("Bank Transfer");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setReference("");
    if (result?.draft) {
      setMethod(result.draft.method || "Bank Transfer");
      setPaymentDate(
        result.draft.paymentDate || new Date().toISOString().slice(0, 10)
      );
      setReference(result.draft.reference || "");
    }
    setErrors({});
  }, [open, outstanding, result]);

  if (!open) return null;

  const validate = () => {
    const next = {};
    const numericAmount = Number(amount);
    if (!amount || !Number.isFinite(numericAmount) || numericAmount <= 0)
      next.amount = "This field is required";
    else if (numericAmount > outstanding)
      next.amount = `Cannot exceed ${money.format(outstanding)}`;
    else if (Math.abs(numericAmount - outstanding) >= 0.005)
      next.amount =
        "The current RCTI settlement flow only records the full outstanding amount.";
    if (!PAYMENT_METHODS.includes(method))
      next.method = "Select a valid payment method.";
    if (!paymentDate) next.paymentDate = "Payment date is required.";
    if (method !== "Cash" && !reference.trim())
      next.reference = "Reference is required for this payment method.";
    if (!fullSettlement)
      next.integration =
        "Additional or partial RCTI payments are not supported by the current settlement API.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = (event) => {
    event.preventDefault();
    if (validate()) onContinue({ amount, method, paymentDate, reference });
  };
  const isSuccess = result?.type === "success";
  const isFailure = result?.type === "failure";
  const modalTitle = isSuccess
    ? "Payment recorded"
    : isFailure
    ? "Couldn’t record payment"
    : "Record payment";

  return (
    <Modal open hideBackdrop disableEnforceFocus onClose={onClose}>
    <div
      className={`rcti-payment-backdrop${dark ? " dark" : ""}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="rcti-payment-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rcti-payment-title"
      >
        <header className="rcti-payment-dialog-header">
          <h2 className="type-h4" id="rcti-payment-title">
            {modalTitle}
          </h2>
          <button type="button" aria-label="Close dialog" onClick={onClose}>
            <LuX />
          </button>
        </header>
        <form onSubmit={submit} noValidate>
          <div className="rcti-payment-dialog-body">
            <div className="rcti-payment-summary">
              <div className="rcti-payment-summary-top">
                <span>{values.rctiNumber || "RCTI"}</span>
                <span
                  className={`rcti-status ${
                    isSuccess ? "status-paid" : "status-payment-due"
                  }`}
                >
                  {isSuccess ? "Paid" : "Payment due"}
                </span>
              </div>
              <div className="rcti-payment-summary-main">
                <span className="rcti-type-emphasis">{supplierName}</span>
                <span>
                  Outstanding{" "}
                  {money.format(
                    isSuccess
                      ? Math.max(0, outstanding - Number(amount || 0))
                      : outstanding
                  )}{" "}
                  {currencyCode}
                </span>
              </div>
              <p>
                {isSuccess
                  ? "The recorded payment is reflected in the RCTI details."
                  : "Record a payment against the outstanding RCTI payable."}
              </p>
            </div>

            {isSuccess ? (
              <div className="rcti-payment-result success" role="status">
                <LuCheck />
                <div>
                  <span className="rcti-type-emphasis">
                    {money.format(amount)} was recorded successfully.
                  </span>
                  <span>
                    The RCTI is fully settled. Its status and balance were
                    refreshed from the server.
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className="rcti-payment-fields">
                  <label>
                    Payment amount
                    <div className="rcti-payment-input-with-prefix">
                      <span>{currencyCode}</span>
                      <input
                        aria-label="Payment amount"
                        inputMode="decimal"
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={amount}
                        onChange={(event) => setAmount(event.target.value)}
                        aria-invalid={Boolean(errors.amount)}
                      />
                    </div>
                    {errors.amount && (
                      <small className="field-error">{errors.amount}</small>
                    )}
                  </label>
                  <label>
                    Payment method
                    <InvoiceSelect dark={dark}
                      aria-label="Payment method"
                      value={method}
                      onChange={(event) => setMethod(event.target.value)}
                      aria-invalid={Boolean(errors.method)}
                    >
                      <option value="">Select payment method</option>
                      {PAYMENT_METHODS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </InvoiceSelect>
                    {errors.method && (
                      <small className="field-error">{errors.method}</small>
                    )}
                  </label>
                  <PickDate
                    label="Payment date"
                    selected={
                      paymentDate
                        ? new Date(
                            paymentDate.includes("T")
                              ? paymentDate
                              : `${paymentDate}T00:00:00`
                          )
                        : null
                    }
                    onChange={(date) => {
                      if (date) {
                        const y = date.getFullYear();
                        const m = String(date.getMonth() + 1).padStart(2, "0");
                        const d = String(date.getDate()).padStart(2, "0");
                        setPaymentDate(`${y}-${m}-${d}`);
                      } else {
                        setPaymentDate("");
                      }
                    }}
                    error={Boolean(errors.paymentDate)}
                    helperText={errors.paymentDate}
                    dateFormat="MMM d, yyyy"
                  />
                  <label className="rcti-payment-reference">
                    Reference{method === "Cash" ? " (optional)" : ""}
                    <input
                      type="text"
                      value={reference}
                      onChange={(event) => setReference(event.target.value)}
                      placeholder="Transaction or bank reference"
                      aria-invalid={Boolean(errors.reference)}
                    />
                    {errors.reference && (
                      <small className="field-error">{errors.reference}</small>
                    )}
                  </label>
                </div>
                {(!fullSettlement || errors.integration) && (
                  <div className="rcti-payment-result info" role="status">
                    <LuCreditCard />
                    <div>
                      <span className="rcti-type-emphasis">
                        Settlement limit
                      </span>
                      <span>
                        {errors.integration ||
                          "The current RCTI settlement API supports only a first, full settlement. No balance will be changed here."}
                      </span>
                    </div>
                  </div>
                )}
                {isFailure && (
                  <div className="rcti-payment-result error" role="alert">
                    <div>
                      <span className="rcti-type-emphasis">
                        {result.message ||
                          "The payment was not recorded. No payable balance was changed."}
                      </span>
                      <span>
                        Review the details and retry. Your entered values have
                        been preserved.
                      </span>
                    </div>
                  </div>
                )}
                <p className="rcti-payment-disclaimer">
                  This workflow opens the existing RCTI settlement editor. The
                  current API records a full settlement only; it does not
                  support partial payments or payment reconciliation.
                </p>
              </>
            )}
          </div>
          <footer className="rcti-payment-dialog-actions">
            {isSuccess ? (
              <>
                <button type="button" onClick={onClose}>
                  Close
                </button>
                <button type="button" className="primary" onClick={onView}>
                  View RCTI
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary"
                  disabled={!fullSettlement || result?.type === "saving"}
                >
                  {result?.type === "saving" ? (
                    <>
                      <LuLoaderCircle className="is-spinning" />
                      Saving payment…
                    </>
                  ) : isFailure ? (
                    "Retry record payment"
                  ) : (
                    "Continue to settlement"
                  )}
                </button>
              </>
            )}
          </footer>
        </form>
      </section>
    </div>
    </Modal>
  );
}

export default RCTISettlementDialog;
